import { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import {
  ArrowLeft,
  Check,
  ChevronDown,
  Eye,
  FolderKanban,
  FlaskConical,
  KeyRound,
  LayoutDashboard,
  LogOut,
  Menu,
  Moon,
  Plus,
  Settings as SettingsIcon,
  Shield,
  ShieldCheck,
  SlidersHorizontal,
  Sun,
  Trash2,
  CalendarClock,
  User as UserIcon,
  Users,
  Building2,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useWorkspace } from "@/context/WorkspaceContext";
import { useTheme } from "@/context/ThemeContext";
import { ProbeLogo } from "@/components/ProbeLogo";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "@/components/ui/sheet";
import { Input } from "@/components/ui/input";

const CUSTOMER_NAV = [
  { to: "/projects", label: "Projects", icon: FolderKanban, testId: "nav-projects-link" },
  { to: "/tests", label: "Test Cases", icon: FlaskConical, testId: "nav-all-tests-link" },
  { to: "/schedules", label: "Schedules", icon: CalendarClock, testId: "nav-schedules-link" },
  { to: "/workspace/members", label: "Team Members", icon: Users, testId: "nav-team-members-link" },
  { to: "/workspace/settings", label: "Workspace Settings", icon: SettingsIcon, testId: "nav-workspace-settings-link" },
  { to: "/bin", label: "Recycle Bin", icon: Trash2, testId: "nav-bin-link" },
  { to: "/profile", label: "My Profile", icon: UserIcon, testId: "nav-profile-link" },
];
const ADMIN_NAV = [
  { to: "/admin/customers", label: "Customers", icon: Users, testId: "nav-admin-customers-link" },
  { to: "/admin/workspaces", label: "Workspaces", icon: Building2, testId: "nav-admin-workspaces-link" },
  { to: "/admin/admins", label: "Super Admins", icon: ShieldCheck, testId: "nav-admin-admins-link" },
  { to: "/admin/platform-settings", label: "Platform Settings", icon: SlidersHorizontal, testId: "nav-admin-platform-link" },
  { to: "/profile", label: "My Profile", icon: UserIcon, testId: "nav-admin-profile-link" },
];

const initialsOf = (email) => {
  if (!email) return "?";
  const part = email.split("@")[0] || "";
  return ((part[0] || "?") + (part.split(/[._-]/)[1]?.[0] || part[1] || "")).toUpperCase();
};

const WorkspaceSwitcher = () => {
  const { workspaces, current, switchTo, refresh } = useWorkspace();
  const [open, setOpen] = useState(false);
  const [creating, setCreating] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [name, setName] = useState("");
  const navigate = useNavigate();

  if (!workspaces || workspaces.length === 0) return null;

  const handleCreate = async () => {
    if (!name.trim()) return;
    setCreating(true);
    try {
      const { data } = await api.post("/workspaces", { name: name.trim() });
      localStorage.setItem("probe_workspace_id", data.id);
      switchTo(data.id);
      await refresh();
      setCreateOpen(false);
      setName("");
      toast.success(`Switched to ${data.name}`);
      // Always land in customer-view after switching workspaces — `/` would
      // bounce super admins back into the admin panel and break the intent.
      navigate("/projects");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setCreating(false);
    }
  };

  return (
    <>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <button
            data-testid="workspace-switcher-button"
            className="flex items-center gap-2 w-full px-2.5 py-2 rounded-lg border border-white/10 bg-white/[0.04] hover:bg-white/[0.08] transition-colors text-left"
          >
            <span className="flex items-center justify-center w-7 h-7 rounded-md bg-[#3B82F6] text-white font-['Geist'] font-semibold text-[11px] shrink-0">
              {(current?.name || "?").slice(0, 2).toUpperCase()}
            </span>
            <span className="flex flex-col min-w-0 flex-1">
              <span className="text-[10px] text-white/40 leading-none">Workspace</span>
              <span className="text-sm font-medium text-white truncate mt-0.5" data-testid="current-workspace-name">
                {current?.name || "Select workspace"}
              </span>
            </span>
            <ChevronDown size={13} className="text-white/50 shrink-0" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="start" className="w-64 rounded-lg border-[#E5E7EB] p-1" data-testid="workspace-menu">
          <DropdownMenuLabel className="px-2 py-1.5 text-xs font-medium text-[#64748B]">
            Workspaces
          </DropdownMenuLabel>
          {workspaces.map((w) => (
            <DropdownMenuItem
              key={w.id}
              onClick={() => { switchTo(w.id); setOpen(false); navigate("/projects"); }}
              data-testid={`workspace-item-${w.id}`}
              className="rounded-md px-2 py-2 text-sm cursor-pointer focus:bg-[#F1F5F9] flex items-center justify-between gap-2"
            >
              <span className="flex items-center gap-2 min-w-0">
                <span className="text-xs text-[#94A3B8] shrink-0 capitalize">{w.my_role}</span>
                <span className="truncate text-[#0F172A]">{w.name}</span>
              </span>
              {w.id === current?.id && <Check size={14} className="text-[#3B82F6] shrink-0" />}
            </DropdownMenuItem>
          ))}
          <DropdownMenuSeparator className="my-1 bg-[#E5E7EB]" />
          <DropdownMenuItem
            onClick={() => { setOpen(false); setCreateOpen(true); }}
            data-testid="create-workspace-trigger"
            className="rounded-md px-2 py-2 text-sm cursor-pointer focus:bg-[#F1F5F9]"
          >
            <Plus size={14} className="mr-2" />
            New workspace
          </DropdownMenuItem>
          {current && (
            <DropdownMenuItem
              onClick={() => { setOpen(false); navigate("/workspace/settings"); }}
              data-testid="workspace-settings-link"
              className="rounded-md px-2 py-2 text-sm cursor-pointer focus:bg-[#F1F5F9]"
            >
              <Users size={14} className="mr-2" />
              Members &amp; settings
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="rounded-xl border-[#E5E7EB]" data-testid="create-workspace-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Geist'] font-semibold tracking-tight">New workspace</DialogTitle>
            <DialogDescription>
              Create a fresh workspace. You&apos;ll be the owner and can invite teammates after.
            </DialogDescription>
          </DialogHeader>
          <Input
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Acme QA"
            className="rounded-md border-[#E5E7EB] focus-visible:ring-[#3B82F6]"
            data-testid="new-workspace-name-input"
          />
          <DialogFooter>
            <button
              onClick={() => setCreateOpen(false)}
              className="h-9 px-4 text-sm font-medium border border-[#E5E7EB] rounded-md bg-white hover:bg-[#F8FAFC]"
            >
              Cancel
            </button>
            <button
              onClick={handleCreate}
              disabled={creating || !name.trim()}
              data-testid="create-workspace-submit"
              className="h-9 px-4 text-sm font-medium rounded-md bg-[#3B82F6] text-white hover:bg-[#2563EB] disabled:opacity-60"
            >
              {creating ? "Creating…" : "Create workspace"}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

const NavList = ({ items, currentPath, testIdPrefix = "", onNavigate }) => (
  <nav className="flex flex-col gap-0.5 px-3" data-testid={`${testIdPrefix}sidebar-nav`}>
    {items.map((item) => {
      const Icon = item.icon;
      const active =
        item.to === "/projects"
          ? currentPath === "/" || currentPath === "/projects" || currentPath.startsWith("/projects/") || currentPath.startsWith("/suite-runs")
          : item.to === "/tests"
            ? currentPath === "/tests" || currentPath.startsWith("/tests/") || currentPath.startsWith("/runs/")
            : currentPath.startsWith(item.to);
      return (
        <Link
          key={item.to}
          to={item.to}
          onClick={onNavigate}
          data-testid={`${testIdPrefix}${item.testId}`}
          className={`flex items-center gap-2.5 px-3 h-9 rounded-md text-sm font-medium transition-colors ${
            active
              ? "bg-[#3B82F6]/15 text-white"
              : "text-white/65 hover:text-white hover:bg-white/[0.06]"
          }`}
        >
          {active && (
            <span className="absolute left-0 w-1 h-5 rounded-r bg-[#3B82F6]" aria-hidden="true" />
          )}
          {Icon && <Icon size={15} className={active ? "text-[#3B82F6]" : "text-white/55"} />}
          <span className="truncate">{item.label}</span>
        </Link>
      );
    })}
  </nav>
);

export const AppShell = ({ children }) => {
  const { user, logout, endEmulation } = useAuth();
  const { current: currentWorkspace } = useWorkspace();
  const { theme, setTheme } = useTheme();
  const location = useLocation();
  const navigate = useNavigate();
  const [endingEmulation, setEndingEmulation] = useState(false);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const isSuperAdmin = user?.global_role === "super_admin";
  const isEmulating = !!user?.emulator;
  const onAdminRoute = location.pathname.startsWith("/admin");
  const onOwnAccountRoute = location.pathname.startsWith("/profile");
  // Super admins should stay in Admin context on their own pages (e.g. /profile),
  // unless they are actively impersonating a customer.
  const inAdminContext = isSuperAdmin && !isEmulating && (onAdminRoute || onOwnAccountRoute);
  const navItems = inAdminContext ? ADMIN_NAV : CUSTOMER_NAV;

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const handleEndEmulation = async () => {
    setEndingEmulation(true);
    try {
      await endEmulation();
      toast.success("Returned to admin");
      navigate("/admin/customers");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setEndingEmulation(false);
    }
  };

  const Sidebar = (
    <aside className="flex flex-col h-full bg-[#0B0B0C] text-white w-60 shrink-0">
      <div className="px-5 pt-5 pb-4">
        <Link to={inAdminContext ? "/admin/customers" : "/projects"} className="flex items-center" data-testid="brand-logo-link" onClick={() => setMobileNavOpen(false)}>
          <ProbeLogo size="md" variant="light" />
        </Link>
      </div>
      {!inAdminContext && (
        <div className="px-3 pb-4">
          <WorkspaceSwitcher />
        </div>
      )}
      {inAdminContext && (
        <div className="px-5 pb-4">
          <p className="text-[10px] font-medium uppercase tracking-wider text-white/35">Super Admin</p>
        </div>
      )}
      <NavList items={navItems} currentPath={location.pathname} onNavigate={() => setMobileNavOpen(false)} />

      {/* Customer View / Admin View — sidebar-styled actions for super admins. */}
      {isSuperAdmin && inAdminContext && (
        <nav className="flex flex-col gap-0.5 px-3 mt-0.5">
          <button
            type="button"
            onClick={() => navigate("/projects")}
            data-testid="sidebar-customer-view"
            className="flex items-center gap-2.5 px-3 h-9 rounded-md text-sm font-medium transition-colors text-left text-white/65 hover:text-white hover:bg-white/[0.06]"
          >
            <Shield size={15} className="text-white/55" />
            <span className="truncate">Customer View</span>
          </button>
        </nav>
      )}
      {isSuperAdmin && !inAdminContext && !isEmulating && (
        <nav className="flex flex-col gap-0.5 px-3 mt-0.5">
          <button
            type="button"
            onClick={() => navigate("/admin/customers")}
            data-testid="sidebar-admin-view"
            className="flex items-center gap-2.5 px-3 h-9 rounded-md text-sm font-medium transition-colors text-left text-white/65 hover:text-white hover:bg-white/[0.06]"
          >
            <Shield size={15} className="text-white/55" />
            <span className="truncate">Admin View</span>
          </button>
        </nav>
      )}
    </aside>
  );

  return (
    <div className="h-screen flex flex-col bg-[#F8FAFC] overflow-hidden">
      {isEmulating && (
        <div
          data-testid="emulation-banner"
          className="bg-[#EFF6FF] border-b border-[#BFDBFE] text-[#1D4ED8] shrink-0"
        >
          <div className="px-4 sm:px-6 h-10 flex items-center justify-between gap-3">
            <div className="flex items-center gap-2 min-w-0 text-sm">
              <Eye size={13} className="shrink-0" />
              <span className="text-[#1D4ED8]/80 shrink-0">Viewing as</span>
              <span className="font-semibold truncate" data-testid="emulation-target-email">
                {currentWorkspace?.name || user.email}
              </span>
              <span className="hidden sm:inline text-[#1D4ED8]/60 truncate text-xs">
                · {user.email}
              </span>
            </div>
            <button
              onClick={handleEndEmulation}
              disabled={endingEmulation}
              data-testid="end-emulation-button"
              className="inline-flex items-center gap-1.5 bg-[#3B82F6] text-white px-3 h-7 rounded-md text-xs font-semibold hover:bg-[#2563EB] disabled:opacity-60 transition-colors shadow-sm"
            >
              <ArrowLeft size={12} />
              {endingEmulation
                ? "Returning…"
                : (user.emulator?.global_role !== "super_admin" ? "Return to my account" : "Return to Admin")}
            </button>
          </div>
        </div>
      )}
      {isSuperAdmin && !isEmulating && !onAdminRoute && !onOwnAccountRoute && null}

      <div className="flex flex-1 min-h-0 overflow-hidden">
        {/* Desktop sidebar — fixed within the row, full height */}
        <div className="hidden md:flex shrink-0 h-full">{Sidebar}</div>

        {/* Mobile top bar + drawer */}
        <div className="md:hidden flex flex-col flex-1 min-w-0 min-h-0">
          <header className="bg-[#0B0B0C] text-white border-b border-white/[0.06] shrink-0">
            <div className="px-4 flex items-center h-14 gap-3">
              <Sheet open={mobileNavOpen} onOpenChange={setMobileNavOpen}>
                <SheetTrigger asChild>
                  <button
                    data-testid="mobile-menu-button"
                    aria-label="Open menu"
                    className="flex items-center justify-center w-9 h-9 rounded-md border border-white/10 bg-white/[0.04] hover:bg-white/[0.08]"
                  >
                    <Menu size={16} />
                  </button>
                </SheetTrigger>
                <SheetContent side="left" className="w-64 p-0 border-r-0 bg-[#0B0B0C]" data-testid="mobile-menu-sheet">
                  <SheetHeader className="sr-only">
                    <SheetTitle>Navigation</SheetTitle>
                  </SheetHeader>
                  <div className="h-full">{Sidebar}</div>
                </SheetContent>
              </Sheet>
              <Link to={inAdminContext ? "/admin/customers" : "/projects"}>
                <ProbeLogo size="md" variant="light" />
              </Link>
            </div>
          </header>
          <main className="flex-1 min-w-0 min-h-0 overflow-y-auto">{children}</main>
        </div>

        {/* Desktop main — only this scrolls */}
        <main className="hidden md:block flex-1 min-w-0 min-h-0 overflow-y-auto">{children}</main>
      </div>

      {/* Floating utilities — fixed top-right of the viewport, always visible.
          Theme flip on the left, user avatar dropdown (My Profile + Log out)
          on the right. When emulating, drop them BELOW the 40px banner so
          they sit cleanly in the white content area (banner: 0–40px → icons
          start at 52px). */}
      <div className={`fixed right-3 z-50 flex items-center gap-2 ${isEmulating ? "top-[52px]" : "top-3"}`}>
        <button
          type="button"
          onClick={() => setTheme(theme === "dark" ? "light" : "dark")}
          data-testid="theme-flip"
          aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          title={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
          className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-[#E5E7EB] bg-white text-[#475569] shadow-sm hover:bg-[#F8FAFC] hover:text-[#0F172A] transition-colors dark:bg-[#1F1F23] dark:border-white/10 dark:text-white/70 dark:hover:bg-[#2A2A2F] dark:hover:text-white"
        >
          {theme === "dark" ? <Sun size={15} /> : <Moon size={15} />}
        </button>

        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              data-testid="header-user-avatar"
              aria-label="Open account menu"
              title={user?.email}
              className="inline-flex items-center justify-center w-9 h-9 rounded-md border border-[#E5E7EB] bg-white shadow-sm hover:bg-[#F8FAFC] transition-colors dark:bg-[#1F1F23] dark:border-white/10 dark:hover:bg-[#2A2A2F] overflow-hidden"
            >
              {user?.avatar_url ? (
                <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
              ) : (
                <span className="font-['Geist'] font-semibold text-[12px] text-[#0F172A] dark:text-white">
                  {initialsOf(user?.email)}
                </span>
              )}
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="end"
            sideOffset={8}
            className="w-64 rounded-xl border-[#E5E7EB] bg-white p-0 overflow-hidden shadow-lg dark:bg-[#161618] dark:border-white/10 dark:shadow-2xl dark:shadow-black/40"
            data-testid="header-user-menu"
          >
            {/* Hero header — avatar + name + role pill on a tinted surface */}
            <div className="px-4 pt-4 pb-3 bg-gradient-to-br from-[#F8FAFC] to-[#EFF6FF] border-b border-[#E5E7EB] dark:from-[#1F1F23] dark:to-[#1D2940] dark:border-white/10">
              <div className="flex items-center gap-3">
                <span
                  className={`flex items-center justify-center w-10 h-10 rounded-full font-['Geist'] font-semibold text-sm shrink-0 overflow-hidden ${
                    isSuperAdmin
                      ? "bg-gradient-to-br from-[#3B82F6] to-[#1D4ED8] text-white ring-2 ring-white shadow-sm dark:ring-white/10"
                      : "bg-[#0F172A] text-white dark:bg-white/10 dark:text-white"
                  }`}
                >
                  {user?.avatar_url ? (
                    <img src={user.avatar_url} alt="" className="w-full h-full object-cover" />
                  ) : (
                    initialsOf(user?.email)
                  )}
                </span>
                <div className="min-w-0 flex-1">
                  {user?.name && (
                    <p className="text-sm font-semibold text-[#0F172A] truncate leading-tight dark:text-white">{user.name}</p>
                  )}
                  <p
                    className="text-xs text-[#475569] truncate leading-snug dark:text-white/60"
                    title={user?.email}
                    data-testid="header-user-menu-email"
                  >
                    {user?.email}
                  </p>
                </div>
              </div>
              {isSuperAdmin && (
                <span className="inline-flex items-center gap-1 mt-3 px-2 py-0.5 rounded-full bg-[#3B82F6]/10 border border-[#3B82F6]/25 text-[10px] font-semibold uppercase tracking-wide text-[#1D4ED8] dark:bg-[#3B82F6]/15 dark:border-[#3B82F6]/40 dark:text-[#93C5FD]">
                  <Shield size={10} />
                  Super Admin
                </span>
              )}
            </div>

            <div className="p-1.5 bg-white dark:bg-[#161618]">
              <DropdownMenuItem
                onClick={handleLogout}
                data-testid="header-menu-logout"
                className="rounded-lg px-2.5 py-2 text-sm cursor-pointer text-[#B91C1C] focus:bg-[#FEF2F2] hover:bg-[#FEF2F2] gap-2 dark:text-[#FCA5A5] dark:focus:bg-[#7F1D1D]/30 dark:hover:bg-[#7F1D1D]/30 dark:focus:text-[#FECACA]"
              >
                <LogOut size={14} />
                <span className="font-medium">Log out</span>
              </DropdownMenuItem>
            </div>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
};
